import React, { useCallback, useContext, useEffect, useState } from 'react';
import PageActionContext from '@stores/page-action-context';
import AppDataContext from '@stores/app-data-context';
import useForm, { buildInput } from '@ui/forms/use-form';
import FormHtmlInput from '@ui/forms/form-input';

const FormLoanDetails = ({ data }) => {
    const { vehiclePrice, deposit, baloonPayment, loanTerm, minLoanAmount, maxLoanAmount,loanAmountRangeErrorMessage } = data;
    const [loanAmount, setLoanAmount] = useState(0);
    const [loanAmountIsValid, setLoanAmountIsValid] = useState(true);
    const pageActionCxt = useContext(PageActionContext);
    const appDataCxt = useContext(AppDataContext);
    const cxtData = appDataCxt.getLoanRequirements() ?? {};

    const validateDepositAmount = ({ value, others }) => {
        const deposit = parseFloat(value, 10);
        const vehiclePrice = parseFloat(others.vehiclePrice, 10);
        validateLoanAmount(vehiclePrice - deposit);
        setLoanAmount(vehiclePrice - deposit);
        return vehiclePrice - minLoanAmount >= deposit;
    };

    const validateVehiclePrice = ({ value, others }) => {
        const deposit = parseFloat(others.deposit, 10);
        const loanValue = parseFloat(value, 10);

        if (deposit > 0) {
            validateLoanAmount(loanValue - deposit);
            setLoanAmount(loanValue - deposit);
            return deposit <= loanValue - minLoanAmount;
        } else {
            validateLoanAmount(parseFloat(others.vehiclePrice, 10));
            setLoanAmount(parseFloat(others.vehiclePrice, 10));
            return true;
        }
    };

    const validateLoanAmount = (value) => {
        setLoanAmountIsValid((value >= minLoanAmount && value <= maxLoanAmount))
        return loanAmountIsValid;
    };
    

    const inputs = [
        buildInput(vehiclePrice, cxtData.vehiclePrice, [
            {
                name: 'vehicle-minimum-amount-error',
                message: data.vehicleMinimumAmountError,
                check: useCallback((value) => validateVehiclePrice(value)),
            },
        ]),
        buildInput(deposit, cxtData.deposit, [
            {
                name: 'loan-deposit-error',
                message: data.loanDepositError,
                check: useCallback((value) => validateDepositAmount(value)),
            },
        ]),
        buildInput(baloonPayment, cxtData.baloonPayment),
        buildInput(loanTerm, cxtData.loanTerm),
    ];

    const form = useForm({
        inputs: inputs,
    });

    useEffect(() => {
        pageActionCxt.reset();
    }, []);

    useEffect(() => {
        pageActionCxt.setNextDisabled(!form.isValid || !loanAmountIsValid);
    }, [form.isValid, loanAmountIsValid]);

    const handleSubmit = (e) => {
        e?.preventDefault();
        if (pageActionCxt.isNextDisabled) return;

        appDataCxt.setLoanRequirements(
            {
                vehiclePrice: parseInt(form.values.vehiclePrice, 10) ?? 0,
                loanAmount: parseInt(loanAmount, 10) ?? 0,
                deposit: parseInt(form.values.deposit, 10) ?? 0,
                baloonPayment: parseInt(form.values.baloonPayment, 10) ?? 0,
                loanTerm: parseInt(form.values.loanTerm, 10) ?? 0,
            }, 
            form.dirty);

        const nextEventData = pageActionCxt.nextEventData;

        if (nextEventData) {
            pageActionCxt.navigate(nextEventData.url, nextEventData.isExternal);
        }
    };

    useEffect(() => {
        if (pageActionCxt.isNextClicked) {
            handleSubmit(null);
            pageActionCxt.setNextClicked(false);
        }
    }, [pageActionCxt.isNextClicked]);

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6 w-full">
            <FormHtmlInput autoFocus={true} data={vehiclePrice} form={form} />
            <FormHtmlInput data={deposit} form={form} />
            <div>
                <div className="flex gap-4 h-12 bg-skin-primary-light rounded-md border border-skin-primary w-full px-4 py-4">
                    <div className="flex w-full text-center justify-center gap-2">
                        <p className="flex items-center text-lg">
                            {data.loanAmount.label}
                        </p>
                        <p className="flex items-center text-2xl text-skin-primary font-semibold">{`${
                            data.loanAmount.prefixSign
                        }${
                            loanAmount > 0 ? loanAmount.toLocaleString() : 0
                        }`}</p>
                    </div>
                </div>
                {!loanAmountIsValid && <div class="text-red-600 text-sm">{loanAmountRangeErrorMessage}</div>}
                {loanAmountIsValid && <div className="text-center text-xs pt-1">{data?.loanAmount?.disclaimer}</div>}
                
            </div>

            <div className="md:flex md:gap-2 md:items-start">
                <div className="md:w-1/2 md:min-w-fit">
                    <FormHtmlInput data={baloonPayment} form={form} />
                </div>
                <div className="md:w-1/2">
                    <FormHtmlInput data={loanTerm} form={form} />
                </div>
            </div>
            <button type="submit" className="hidden">
                Submit
            </button>
        </form>
    );
};

export default FormLoanDetails;
